import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../lib/api";
import { API } from "../App";
import { Lock, CreditCard, Check, Zap } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";

export default function Paywall() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [interval, setInterval] = useState("month");
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trialEligible, setTrialEligible] = useState(true); // default true until we know

  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      axios.get(`${API}/billing/plans`),
      axios.get(`${API}/billing/subscription-status`, { headers, withCredentials: true }).catch(() => null)
    ]).then(([plansRes, subRes]) => {
      setPlans(plansRes.data.plans);
      if (subRes?.data) {
        setTrialEligible(subRes.data.trial_eligible !== false);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleCheckout = async (planKey) => {
    setCheckoutLoading(planKey);
    try {
      const token = localStorage.getItem("auth_token");
      const res = await axios.post(
        `${API}/billing/create-checkout-session`,
        { plan_key: planKey, interval, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear sesión de pago");
      setCheckoutLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4 py-12" data-testid="paywall-screen">
      <div className="text-center max-w-2xl mx-auto mb-10">
        <div className="w-16 h-16 bg-[#C8192C]/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Lock className="w-8 h-8 text-[#C8192C]" />
        </div>
        {trialEligible ? (
          <>
            <h1 className="text-3xl font-bold text-slate-900 mb-3" data-testid="paywall-trial-title">
              14 días gratis, sin compromiso
            </h1>
            <p className="text-slate-500 text-lg mb-4">
              Elige tu plan y empieza a ver la rentabilidad real de tu negocio hoy mismo.
              Tu tarjeta no se cargará hasta que termine la prueba.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-500" /> 14 días de prueba gratis</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-500" /> Tarjeta requerida para activar</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-500" /> Cancela en cualquier momento</span>
            </div>
          </>
        ) : (
          <>
            <h1 className="text-3xl font-bold text-slate-900 mb-3" data-testid="paywall-subscribe-title">
              Activa tu suscripción
            </h1>
            <p className="text-slate-500 text-lg mb-4">
              Elige el plan que mejor se adapte a tu negocio para seguir accediendo a ProfitSystem.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-500" /> Acceso completo desde el primer día</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-500" /> Cancela en cualquier momento</span>
            </div>
          </>
        )}
      </div>

      {/* Interval toggle */}
      <div className="flex items-center bg-white rounded-lg p-1 shadow-sm border mb-8" data-testid="paywall-interval-toggle">
        <button
          className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
            interval === "month" ? "bg-slate-900 text-white" : "text-slate-500"
          }`}
          onClick={() => setInterval("month")}
        >
          Mensual
        </button>
        <button
          className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
            interval === "year" ? "bg-slate-900 text-white" : "text-slate-500"
          }`}
          onClick={() => setInterval("year")}
        >
          Anual <span className="text-green-400 text-xs ml-1">-20%</span>
        </button>
      </div>

      {/* Plans */}
      <div className="grid md:grid-cols-3 gap-6 max-w-5xl w-full" data-testid="paywall-plans-grid">
        {plans.map((plan) => {
          const price = interval === "year" ? plan.annual_price : plan.price;
          const perMonth = interval === "year" ? (plan.annual_price / 12).toFixed(0) : plan.price;

          return (
            <Card
              key={plan.plan_key}
              className={`relative overflow-hidden ${
                plan.plan_key === "growth" ? "border-blue-300 shadow-lg scale-[1.02]" : "shadow-sm"
              }`}
              data-testid={`paywall-plan-${plan.plan_key}`}
            >
              {plan.plan_key === "growth" && (
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                  Recomendado
                </div>
              )}
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                <div className="mt-2">
                  <span className="text-4xl font-bold text-slate-900">{perMonth}€</span>
                  <span className="text-slate-500">/mes</span>
                  {interval === "year" && (
                    <p className="text-xs text-slate-400 mt-1">Facturado {price}€/año</p>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    {plan.features.max_orders_per_year?.toLocaleString("es-ES")} pedidos/año
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    {plan.features.max_shops} tienda
                  </li>
                  {plan.features.integrations_enabled?.map((name) => (
                    <li key={name} className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <span className="capitalize">{name.replace(/_/g, " ")}</span>
                    </li>
                  ))}
                  {plan.features.csv_export && (
                    <li className="flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      Exportación CSV
                    </li>
                  )}
                  {plan.features.ai_insights && (
                    <li className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      AI Insights
                    </li>
                  )}
                </ul>
                <Button
                  className={`w-full ${
                    plan.plan_key === "growth" ? "bg-[#C8192C] hover:bg-[#a81424]" : "bg-slate-900 hover:bg-slate-800"
                  } text-white`}
                  onClick={() => handleCheckout(plan.plan_key)}
                  disabled={!!checkoutLoading}
                  data-testid={`paywall-select-${plan.plan_key}`}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {checkoutLoading === plan.plan_key
                    ? "Redirigiendo..."
                    : trialEligible ? "Empezar prueba gratuita" : "Suscribirse"}
                </Button>
                {trialEligible && (
                  <p className="text-center text-xs text-slate-400 mt-1" data-testid="paywall-trial-note">14 días gratis · sin cobro inmediato</p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
